const GOOGLE_IDENTITY_SCRIPT_ID = 'google-identity-services';
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';
const EXCEL_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
];

let tokenClient = null;
let accessToken = null;
let accessTokenExpiresAt = 0;

const TOKEN_STORAGE_KEY = 'wirecraft.googleDriveAccessToken';
const FILE_LIST_CACHE_PREFIX = 'wirecraft.googleDriveFiles';
const TOKEN_EXPIRY_MARGIN_MS = 60 * 1000;
const DRIVE_LIST_PAGE_SIZE = '1000';

const getClientId = () => process.env.REACT_APP_GOOGLE_CLIENT_ID;
const getCurrentOrigin = () => window.location?.origin || 'origine sconosciuta';
const getFileListCacheScope = ({ userId, folderId } = {}) => (
  `${userId || 'anonymous'}:${folderId || 'all'}`
);

const buildGoogleAuthErrorMessage = (response = {}) => {
  const errorCode = response?.error || '';
  const errorDescription = response?.error_description || '';

  if (errorCode === 'popup_closed') {
    return 'Accesso Google annullato prima del completamento.';
  }

  if (errorCode === 'access_denied') {
    return 'Accesso Google negato. Verifica l\'account selezionato e i permessi richiesti.';
  }

  if (
    errorCode === 'origin_mismatch' ||
    errorDescription.toLowerCase().includes('origin_mismatch')
  ) {
    return `Google OAuth rifiuta l'origine corrente: ${getCurrentOrigin()}. Aggiungi questo URL tra gli Authorized JavaScript origins del client OAuth in Google Cloud Console.`;
  }

  return errorDescription || errorCode || 'Errore di autorizzazione Google.';
};

const getCachedAccessToken = () => {
  if (accessToken && accessTokenExpiresAt > Date.now() + TOKEN_EXPIRY_MARGIN_MS) {
    return accessToken;
  }

  try {
    const storedToken = window.sessionStorage?.getItem(TOKEN_STORAGE_KEY);
    if (!storedToken) return null;

    const parsedToken = JSON.parse(storedToken);
    if (parsedToken?.accessToken && parsedToken?.expiresAt > Date.now() + TOKEN_EXPIRY_MARGIN_MS) {
      accessToken = parsedToken.accessToken;
      accessTokenExpiresAt = parsedToken.expiresAt;
      return accessToken;
    }
  } catch (error) {
    window.sessionStorage?.removeItem(TOKEN_STORAGE_KEY);
  }

  accessToken = null;
  accessTokenExpiresAt = 0;
  return null;
};

const saveAccessToken = (token, expiresInSeconds) => {
  accessToken = token;
  accessTokenExpiresAt = Date.now() + ((expiresInSeconds || 3600) * 1000);

  try {
    window.sessionStorage?.setItem(TOKEN_STORAGE_KEY, JSON.stringify({
      accessToken,
      expiresAt: accessTokenExpiresAt
    }));
  } catch (error) {
    // sessionStorage can be disabled in private or restricted browser contexts.
  }
};

const clearAccessToken = () => {
  accessToken = null;
  accessTokenExpiresAt = 0;

  try {
    window.sessionStorage?.removeItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    // Nothing else to do if browser storage is unavailable.
  }
};

const getFileListCacheKey = (config = {}, options = {}) => (
  `${FILE_LIST_CACHE_PREFIX}:${getFileListCacheScope({
    userId: options.userId,
    folderId: config.folderId
  })}`
);

const getCachedFileList = (config = {}, options = {}) => {
  const cacheKey = getFileListCacheKey(config, options);

  try {
    const cachedValue = window.localStorage?.getItem(cacheKey);
    if (!cachedValue) return null;

    const parsedValue = JSON.parse(cachedValue);
    if (!Array.isArray(parsedValue?.files)) return null;

    return parsedValue.files;
  } catch (error) {
    try {
      window.localStorage?.removeItem(cacheKey);
    } catch (storageError) {
      // Ignore local storage cleanup failures.
    }
    return null;
  }
};

const saveFileListCache = (config = {}, options = {}, files = []) => {
  try {
    window.localStorage?.setItem(getFileListCacheKey(config, options), JSON.stringify({
      createdAt: Date.now(),
      files
    }));
  } catch (error) {
    // Cache is only a speed-up. The import flow works without browser storage.
  }
};

const clearFileListCache = (config = {}, options = {}) => {
  try {
    window.localStorage?.removeItem(getFileListCacheKey(config, options));
  } catch (error) {
    // Ignore storage errors.
  }
};

const loadScript = (id, src) => new Promise((resolve, reject) => {
  const existing = document.getElementById(id);
  if (existing) {
    resolve();
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  script.defer = true;
  script.onload = resolve;
  script.onerror = () => reject(new Error('Impossibile caricare Google Identity Services'));
  document.head.appendChild(script);
});

const ensureTokenClient = async () => {
  const clientId = getClientId();
  if (!clientId) {
    throw new Error('Configura REACT_APP_GOOGLE_CLIENT_ID per abilitare Google Drive.');
  }

  await loadScript(GOOGLE_IDENTITY_SCRIPT_ID, 'https://accounts.google.com/gsi/client');

  if (!window.google?.accounts?.oauth2) {
    throw new Error('Google Identity Services non disponibile.');
  }

  if (!tokenClient) {
    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: DRIVE_SCOPE,
      callback: () => {}
    });
  }

  return tokenClient;
};

export const requestDriveAccessToken = async ({ forceConsent = false } = {}) => {
  const cachedToken = !forceConsent ? getCachedAccessToken() : null;
  if (cachedToken) {
    return cachedToken;
  }

  const client = await ensureTokenClient();

  return new Promise((resolve, reject) => {
    client.callback = (response) => {
      if (response?.error) {
        reject(new Error(buildGoogleAuthErrorMessage(response)));
        return;
      }

      saveAccessToken(response.access_token, response.expires_in);
      resolve(accessToken);
    };

    client.requestAccessToken({ prompt: forceConsent ? 'consent' : '' });
  });
};

const driveFetch = async (url, options = {}) => {
  const token = getCachedAccessToken() || await requestDriveAccessToken();
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {})
    }
  });

  if (response.status === 401) {
    clearAccessToken();
    throw new Error('Sessione Google scaduta. Riprova il collegamento a Drive.');
  }

  if (!response.ok) {
    throw new Error(`Errore Google Drive (${response.status})`);
  }

  return response;
};

const buildFolderQuery = (folderId) => {
  if (!folderId) return '';
  return `'${folderId}' in parents and `;
};

const listAllDrivePages = async (params, options = {}) => {
  const files = [];
  let pageToken = '';

  do {
    if (pageToken) {
      params.set('pageToken', pageToken);
    } else {
      params.delete('pageToken');
    }

    const response = await driveFetch(`https://www.googleapis.com/drive/v3/files?${params.toString()}`, {
      cache: options.forceRefresh ? 'no-store' : 'default'
    });
    const data = await response.json();

    files.push(...(data.files || []));
    pageToken = data.nextPageToken || '';
  } while (pageToken);

  return files;
};

export const listDriveFolders = async (folderId = 'root', options = {}) => {
  const params = new URLSearchParams({
    q: `${buildFolderQuery(folderId)}mimeType='application/vnd.google-apps.folder' and trashed=false`,
    pageSize: DRIVE_LIST_PAGE_SIZE,
    orderBy: 'name',
    fields: 'nextPageToken,files(id,name)'
  });

  return listAllDrivePages(params, options);
};

const listDriveFilesInFolder = async (folderId, options = {}) => {
  const mimeQuery = EXCEL_MIME_TYPES
    .map(type => `mimeType='${type}'`)
    .join(' or ');

  const params = new URLSearchParams({
    q: `${buildFolderQuery(folderId)}(${mimeQuery}) and trashed=false`,
    orderBy: 'modifiedTime desc',
    pageSize: DRIVE_LIST_PAGE_SIZE,
    fields: 'nextPageToken,files(id,name,mimeType,modifiedTime,size,webViewLink)'
  });

  return listAllDrivePages(params, options);
};

export const listDriveSpreadsheetFiles = async (config = {}, options = {}) => {
  await requestDriveAccessToken();

  if (options.forceRefresh) {
    clearFileListCache(config, options);
  } else {
    const cachedFiles = getCachedFileList(config, options);
    if (cachedFiles) {
      return cachedFiles;
    }
  }

  if (!config.folderId) {
    const files = await listDriveFilesInFolder(null, options);
    saveFileListCache(config, options, files);
    return files;
  }

  const queue = [config.folderId];
  const allFiles = [];
  const visited = new Set();

  while (queue.length > 0) {
    const folderId = queue.shift();
    if (visited.has(folderId)) continue;
    visited.add(folderId);

    const [files, folders] = await Promise.all([
      listDriveFilesInFolder(folderId, options),
      listDriveFolders(folderId, options)
    ]);

    allFiles.push(...files);
    folders.forEach(folder => queue.push(folder.id));
  }

  const sortedFiles = allFiles.sort((a, b) => new Date(b.modifiedTime || 0) - new Date(a.modifiedTime || 0));
  saveFileListCache(config, options, sortedFiles);
  return sortedFiles;
};

export const downloadDriveSpreadsheetFile = async (driveFile) => {
  const response = await driveFetch(`https://www.googleapis.com/drive/v3/files/${driveFile.id}?alt=media`);
  const blob = await response.blob();

  return new File([blob], driveFile.name, {
    type: driveFile.mimeType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
};

export const isGoogleDriveConfigured = () => Boolean(getClientId());
